<template>
    <v-row class="ma-0">
        <v-col cols="12" sm="4" class="pa-0 pr-sm-3">
            <v-text-field
                :value="value.name"
                @input="update('name', $event)"
                label="Nome politica SLA"
                outlined
                dense
                hide-details="auto"
                class="mb-4"
                :rules="[v => !!v || 'Campo obbligatorio']"
            ></v-text-field>
        </v-col>

        <v-col cols="12" sm="4" class="pa-0 pr-sm-3">
            <v-select
                :value="value.working_hours_config_id"
                @input="update('working_hours_config_id', $event)"
                :items="workingHoursConfigs"
                item-text="name"
                item-value="id"
                label="Orario di lavoro"
                outlined
                dense
                hide-details="auto"
                class="mb-4"
                clearable
                :menu-props="{ 'offset-y': true }"
            ></v-select>
        </v-col>

        <v-col cols="12" sm="4" class="pa-0 d-flex align-center">
            <v-switch
                :input-value="value.is_default"
                @change="update('is_default', $event)"
                label="SLA di default"
                color="primary"
                dense
                hide-details
                class="mt-0"
            ></v-switch>
        </v-col>

        <v-col cols="12" class="pa-0 mt-2">
            <sla-matrix
                ref="slaMatrix"
                :value="matrix"
                @input="onMatrixInput($event)"
                :priorities="priorities"
                :request-types="requestTypes"
            ></sla-matrix>
        </v-col>
    </v-row>
</template>

<script>
import SlaMatrix from './SlaMatrix.vue'

// Props: value — the SLA form object (v-model pattern)
// value.targets is an Array (API format): [{ priority, request_type, response_time_minutes, resolution_time_minutes }]
// Internally the matrix is converted to/from object format for SlaMatrix.
// Emits: input — same shape as value
//
// Usage:
//   <sla-config-editor
//     v-model="form"
//     :priorities="priorities"
//     :request-types="requestTypes"
//     :working-hours-configs="workingHoursConfigs"
//   ></sla-config-editor>
//
// Expose toApiTargets() via ref to get the final targets array before saving.

function targetsToMatrix(targets) {
    const matrix = {}
    if (!targets || !targets.length) return matrix
    targets.forEach(t => {
        if (!matrix[t.priority]) matrix[t.priority] = {}
        const rh = Math.floor((t.response_time_minutes || 0) / 60)
        const rm = (t.response_time_minutes || 0) % 60
        const resH = Math.floor((t.resolution_time_minutes || 0) / 60)
        const resM = (t.resolution_time_minutes || 0) % 60
        matrix[t.priority][t.request_type] = {
            response_hours: rh,
            response_minutes: rm,
            resolution_hours: resH,
            resolution_minutes: resM
        }
    })
    return matrix
}

export default {
    name: 'SlaConfigEditor',

    components: {
        SlaMatrix
    },

    props: {
        value: {
            type: Object,
            default: () => ({
                name: '',
                working_hours_config_id: null,
                is_default: false,
                targets: []
            })
        },
        priorities: {
            type: Array,
            default: () => []
        },
        requestTypes: {
            type: Array,
            default: () => []
        },
        workingHoursConfigs: {
            type: Array,
            default: () => []
        }
    },

    data() {
        return {
            // Internal matrix object used by SlaMatrix component
            matrix: targetsToMatrix(this.value ? this.value.targets : [])
        }
    },

    watch: {
        'value.targets': {
            handler(newTargets) {
                this.matrix = targetsToMatrix(newTargets || [])
            }
        }
    },

    methods: {
        update(field, val) {
            this.$emit('input', { ...this.value, [field]: val })
        },

        onMatrixInput(matrixObj) {
            this.matrix = matrixObj
            // Convert back to targets array and emit
            const targets = this.matrixToTargets(matrixObj)
            this.$emit('input', { ...this.value, targets })
        },

        matrixToTargets(matrixObj) {
            const targets = []
            Object.keys(matrixObj || {}).forEach(priority => {
                Object.keys(matrixObj[priority] || {}).forEach(rtValue => {
                    const cell = matrixObj[priority][rtValue]
                    const rh = parseInt(cell.response_hours || 0, 10)
                    const rm = parseInt(cell.response_minutes || 0, 10)
                    const resH = parseInt(cell.resolution_hours || 0, 10)
                    const resM = parseInt(cell.resolution_minutes || 0, 10)
                    targets.push({
                        priority,
                        request_type: rtValue,
                        response_time_minutes: rh * 60 + rm,
                        resolution_time_minutes: resH * 60 + resM
                    })
                })
            })
            return targets
        },

        // Called by parent via $refs to get full targets array (covers all priority×rt combos)
        toApiTargets() {
            if (this.$refs.slaMatrix) {
                return this.$refs.slaMatrix.toApiTargets()
            }
            return this.matrixToTargets(this.matrix)
        }
    }
}
</script>
